import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { Page, UserNav, LayoutSideNavigation, PrimaryButton, SecondaryButton } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import useSubscription from './useSubscription';
import css from './SubscriptionsPage.module.css';

/**
 * SubscriptionsPage — lets users view and manage their subscription.
 *
 * Plans are defined via env vars:
 *   MONTHLY_PRICE, QUARTERLY_PRICE, YEARLY_PRICE (Stripe Price IDs)
 *   REACT_APP_MONTHLY_PRICE_LABEL, etc. (optional labels, defaults provided)
 *
 * In a real integration, the client would:
 * 1. Create/retrieve a Stripe Customer for the user (via a separate endpoint)
 * 2. Show a Stripe Elements card form to collect payment method
 * 3. Call POST /api/stripe/create-subscription
 *
 * This page provides the structure; Stripe Elements integration is left as
 * client-specific customization (requires Stripe publishable key + UI design).
 */
const PLANS = [
  {
    key: 'MONTHLY_PRICE',
    label: process.env.REACT_APP_MONTHLY_PRICE_LABEL || 'Monthly',
    billing: 'Billed monthly',
  },
  {
    key: 'QUARTERLY_PRICE',
    label: process.env.REACT_APP_QUARTERLY_PRICE_LABEL || 'Quarterly',
    billing: 'Billed every 3 months',
  },
  {
    key: 'YEARLY_PRICE',
    label: process.env.REACT_APP_YEARLY_PRICE_LABEL || 'Yearly',
    billing: 'Billed once a year',
  },
];

const SubscriptionsPage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(state => state.UI?.scrollingDisabled ?? false);
  const { subscription, hasSubscription, status, isCanceled, isPastDue, isTrialing } =
    useSubscription();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const handleCancel = async () => {
    if (!subscription?.id) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: subscription.id }),
      });
      if (!res.ok) throw new Error('Cancel failed');
      window.location.reload();
    } catch (e) {
      setCancelError(e.message);
      setCancelling(false);
    }
  };

  const title = intl.formatMessage({ id: 'SubscriptionsPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation
        topbar={
          <>
            <TopbarContainer
              currentPage="SubscriptionsPage"
              desktopClassName={css.desktopTopbar}
              mobileClassName={css.mobileTopbar}
            />
            <UserNav currentPage="SubscriptionsPage" />
          </>
        }
        sideNav={null}
        useAccountSettingsNav
        currentPage="SubscriptionsPage"
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <h1 className={css.heading}>
            <FormattedMessage id="SubscriptionsPage.heading" />
          </h1>

          {hasSubscription || isTrialing ? (
            <div className={css.activeSubscription}>
              <p className={css.statusBadge}>
                <FormattedMessage
                  id="SubscriptionsPage.activeStatus"
                  values={{ status: subscription?.status }}
                />
              </p>
              {cancelError && <p className={css.error}>{cancelError}</p>}
              <SecondaryButton
                className={css.cancelBtn}
                onClick={handleCancel}
                inProgress={cancelling}
              >
                <FormattedMessage id="SubscriptionsPage.cancelSubscription" />
              </SecondaryButton>
            </div>
          ) : (
            <div className={css.plans}>
              <p className={css.noSubscription}>
                <FormattedMessage
                  id={isCanceled ? 'SubscriptionsPage.canceled' : isPastDue ? 'SubscriptionsPage.pastDue' : 'SubscriptionsPage.noSubscription'}
                />
              </p>
              <div className={css.planCards}>
                {PLANS.map(plan => (
                  <div key={plan.key} className={css.planCard}>
                    <h3>{plan.label}</h3>
                    <p>{plan.billing}</p>
                    <PrimaryButton
                      className={css.planBtn}
                      onClick={() => {
                        // Redirect to checkout with selected plan
                        window.location.href = `/checkout?plan=${plan.key}`;
                      }}
                    >
                      <FormattedMessage id="SubscriptionsPage.subscribe" />
                    </PrimaryButton>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};

export default SubscriptionsPage;
